  import { TableColumn } from 'react-data-table-component';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'client/_redux/reducers';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { CRUDDataTableHandle } from 'dashboard/containers/CRUDDataTable';
import { useTableColumnsWithActions } from 'dashboard/containers/CRUDDataTable/useTableColumnsWithActions';
import { useNavigate } from 'react-router-dom';
import { ITour, ITourFormValues } from 'client/models/ITour';
import { createTourDashboard, getAllToursDashboard } from 'dashboard/_redux/actions/tours';
import { ICategoryTour } from 'client/models/ICategoryTour';
import { clientApi } from 'api';

export const useToursList = () => {
  const [t] = useTranslation();
  const dataTableRef = useRef<CRUDDataTableHandle>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { tours, loaded } = useSelector((state: RootState) => state.dashboardTours);

  // État pour les catégories
  const [categories, setCategories] = useState<ICategoryTour[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Charger les catégories au montage
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        console.log('🔄 Chargement des catégories...');
        const response = await clientApi.get('category-tours', {
          params: { limit: 100 },
        });
        console.log('✅ Réponse API catégories:', response.data);
        const categories = response.data.categoryTours || [];
        console.log('📋 Catégories chargées:', categories);
        setCategories(categories);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des catégories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // État pour le modal de création
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<ITourFormValues>({
    name: '',
    description: '',
    categoryTour: '',
    difficulty: 'medium',
    status: 'draft',
    estimatedDuration: 0,
    distance: 0,
    price: 0,
    tags: [],
    steps: [],
  });

  // Ouvrir le modal de création
  const handleOpenCreateModal = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      categoryTour: '',
      difficulty: 'medium',
      status: 'draft',
      estimatedDuration: 0,
      distance: 0,
      price: 0,
      tags: [],
      steps: [],
    });
    setIsCreateModalOpen(true);
  }, []);

  // Fermer le modal de création
  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  // Gérer les changements dans le formulaire
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'estimatedDuration' || name === 'distance' || name === 'price' 
        ? parseFloat(value) || 0 
        : value,
    }));
  }, []);

  // Soumettre le formulaire de création
  const handleSubmitCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        await dispatch(createTourDashboard(formData) as any);
        handleCloseCreateModal();
        // Rafraîchir la liste après création
        setTimeout(() => {
          dataTableRef.current?.getData();
        }, 500);
      } catch (_error) {
        // L'erreur est déjà toastée via errorHandler + validation toast
      }
    },
    [dispatch, formData, handleCloseCreateModal],
  );

  const onPressShow = useCallback(
    (item: ITour) => {
      navigate(`/dashboard/tour/${item._id}`);
    },
    [navigate],
  );

  const tableCols: TableColumn<ITour>[] = [
    {
      name: t('dashboard.tourPage.name') || 'Nom',
      selector: (e) => e.name,
      sortable: true,
      center: true,
      sortField: 'name',
      grow: 2,
    },
    {
      name: t('dashboard.tourPage.category') || 'Catégorie',
      selector: (e) => typeof e.categoryTour === 'object' ? e.categoryTour.name : '',
      sortable: true,
      center: true,
      sortField: 'categoryTour',
      grow: 1,
    },
    {
      name: t('dashboard.tourPage.difficulty') || 'Difficulté',
      selector: (e) => e.difficulty || 'medium',
      sortable: true,
      center: true,
      sortField: 'difficulty',
      grow: 1,
      cell: (row) => {
        const difficultyMap: Record<string, string> = {
          easy: '🟢 Facile',
          medium: '🟡 Moyen',
          hard: '🔴 Difficile',
        };
        return <span>{difficultyMap[row.difficulty || 'medium']}</span>;
      },
    },
    {
      name: t('dashboard.tourPage.steps') || 'Étapes',
      selector: (e) => e.steps?.length || 0,
      sortable: false,
      center: true,
      grow: 1,
    },
    {
      name: t('dashboard.tourPage.status') || 'Statut',
      selector: (e) => e.status,
      sortable: true,
      center: true,
      sortField: 'status',
      grow: 1,
      cell: (row) => {
        const statusMap: Record<string, string> = {
          draft: '📝 Brouillon',
          published: '✅ Publié',
          archived: '📦 Archivé',
        };
        return <span>{statusMap[row.status]}</span>;
      },
    },
    {
      name: t('dashboard.tourPage.addedBy') || 'Créé par',
      selector: (e) => e.addedBy.username,
      sortable: true,
      center: true,
      sortField: 'addedBy.username',
      grow: 1,
    },
    {
      sortField: 'createdAt',
      name: t('dashboard.tourPage.createdAt') || 'Date de création',
      selector: (e) => (e.createdAt ? format(new Date(e.createdAt), 'dd-MM-yyyy') : ''),
      sortable: true,
      center: true,
      grow: 1,
    },
  ];

  const columns = useTableColumnsWithActions({
    columns: tableCols,
    buttons: [
      {
        fn: onPressShow,
        title: t('dataTable.show') || 'Voir',
        className: 'btn btn-dark btn-xs m-1',
      },
    ],
  });

  return {
    tours,
    loaded,
    tableCols,
    columns,
    dataTableRef,
    isCreateModalOpen,
    formData,
    categories,
    loadingCategories,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleInputChange,
    handleSubmitCreate,
  };
};
